import { BarChart3Icon, BriefcaseIcon, FileTextIcon, LifeBuoyIcon, PlusIcon, ReceiptIcon, SettingsIcon, ShieldCheckIcon, UserPlusIcon, UsersIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, Outlet } from 'react-router';
import { Logo } from '@/components/Logo';
import { TabStrip, type TabItem } from '@/components/TabStrip';
import { Button } from '@/components/ui/button';
import { useUsers } from '@/features/org/queries';
import { useBranding } from '@/lib/branding';
import { useMe } from '@/lib/auth';
import { fmtDate, fmtNumber } from '@/lib/format';

/**
 * Organization frame, the counterpart of the job masthead: the organization's logo and name on one card with a
 * primary-coloured rule across the top, two quiet pills (plan or pilot, your level), one line of facts (people,
 * active jobs), the organization-level actions on the right, and beneath them the organization sections as
 * tabs. Every page under /org starts the same way, so the sidebar and the tabs agree.
 */
export function OrgLayout() {
  const me = useMe();
  const { branding } = useBranding();
  const users = useUsers();
  const f = me.tenant.features;
  const activeJobs = me.jobs.filter((j) => j.status === 'active').length;
  const people = users.data?.items.filter((u) => u.status === 'active').length;

  const tabs: TabItem[] = [
    { to: '/org/users', label: 'Users', icon: UsersIcon, badge: people },
    ...(f.reports ? [{ to: '/org/reports', label: 'Reports', icon: BarChart3Icon }] : []),
    ...(f.helpRequests ? [{ to: '/org/support', label: 'Help requests', icon: LifeBuoyIcon }] : []),
    ...(f.templates ? [{ to: '/org/templates', label: 'Templates', icon: FileTextIcon }] : []),
    ...(f.billing ? [{ to: '/org/billing', label: 'Billing', icon: ReceiptIcon }] : []),
    { to: '/org/settings', label: 'Settings', icon: SettingsIcon },
  ];

  return (
    <div>
      <header className="bg-card border border-b-0 px-4 md:px-6">
        <div className="bg-primary -mx-4 h-0.5 md:-mx-6" aria-hidden />
        <div className="flex items-start justify-between gap-6 pt-4 pb-4 md:pt-5 md:pb-5">
          <div className="flex min-w-0 items-start gap-3.5 md:gap-4">
            <span className="bg-muted mt-0.5 grid size-12 shrink-0 place-items-center overflow-hidden border">{branding.logoUrl ? <Logo size="sm" className="max-h-8 max-w-10" /> : <span className="font-display text-2xl leading-none">{branding.tenantName.slice(0, 1).toUpperCase()}</span>}</span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <h1 className="font-display min-w-0 truncate text-[2rem] leading-none tracking-tight md:text-[2.5rem]">{branding.tenantName}</h1>
                <div className="flex items-center gap-1.5">
                  {me.tenant.pilotEndsOn ? (
                    <Pill>
                      <span className="bg-warning size-1.5 rounded-full" aria-hidden /> Pilot through {fmtDate(me.tenant.pilotEndsOn)}
                    </Pill>
                  ) : (
                    <Pill>
                      <span className="bg-success size-1.5 rounded-full" aria-hidden /> Active
                    </Pill>
                  )}
                  <Pill>
                    <ShieldCheckIcon className="size-3" aria-hidden /> Super User
                  </Pill>
                </div>
              </div>
              <div className="text-muted-foreground mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
                <span className="flex items-center gap-1.5 tabular-nums">
                  <UsersIcon className="size-3.5" aria-hidden />
                  {people === undefined ? '…' : `${fmtNumber(people)} ${people === 1 ? 'person' : 'people'} can sign in`}
                </span>
                <span className="flex items-center gap-1.5 tabular-nums">
                  <BriefcaseIcon className="size-3.5" aria-hidden />
                  {fmtNumber(activeJobs)} active {activeJobs === 1 ? 'job' : 'jobs'}
                </span>
                {branding.supportEmail && <span className="hidden truncate lg:inline">{branding.supportEmail}</span>}
              </div>
            </div>
          </div>
          <div className="hidden shrink-0 items-center gap-2 pt-1 md:flex">
            {me.permissions.createJobs && (
              <Button asChild variant="outline">
                <Link to="/jobs?new=1">
                  <PlusIcon /> New job
                </Link>
              </Button>
            )}
            {me.permissions.manageUsers && (
              <Button asChild>
                <Link to="/org/users?invite=1">
                  <UserPlusIcon /> Invite user
                </Link>
              </Button>
            )}
          </div>
        </div>
      </header>
      <TabStrip ariaLabel="Organization sections" sticky bleed={false} className="bg-card border-x px-2 md:px-3" items={tabs} />
      <div className="pt-6 md:pt-8">
        <Outlet />
      </div>
    </div>
  );
}

function Pill({ children }: { children: ReactNode }) {
  return <span className="text-muted-foreground inline-flex h-6 items-center gap-1.5 border px-2 text-[11px] font-medium whitespace-nowrap">{children}</span>;
}
